import { VCPM } from 'pcs-domain';

/** Session-only state; never persisted into a workload. */
export interface ClusterReuseContext {
  readonly reference: Readonly<VCPM.ClusterReference>;
  readonly vsanArchitecture: string;
  picked: boolean;
}

export function ownedClusterReferences(workload?: VCPM.Workload): { compute?: VCPM.ClusterReference; storage?: VCPM.ClusterReference } {
  const dhci = workload?.vcfDeploymentInputs?.deploymentType === 'dHCI';
  const computeMode = dhci ? workload?.computeClusterHardwareConfiguration?.hardwareConfigMode : workload?.hardwareConfiguration?.hardwareConfigMode;
  const copy = (ref?: VCPM.ClusterReference | null) => ref?.id ? { ...ref } : undefined;
  return {
    compute: computeMode !== 'use-existing-cluster' ? copy(workload?.computeCluster) : undefined,
    storage: dhci && workload?.storageClusterHardwareConfiguration?.hardwareConfigMode !== 'use-existing-cluster' ? copy(workload?.storageCluster) : undefined,
  };
}

/** Owners may select themselves only when another compatible cluster enables reuse.
 * Saved referencers have no owned id, so their current cluster remains usable on its own.
 * The caller supplies the domain-local compute pool or project-wide storage pool. */
export function getEligibleClusterReuseOptions<T extends VCPM.ReusableComputeCluster | VCPM.StorageCluster>(
  pool: readonly T[] | null | undefined,
  kind: 'HCI' | 'COMPUTE' | 'STORAGE',
  architecture: string | undefined,
  ownedId?: string,
): T[] {
  if (!architecture || architecture.toLowerCase() === 'osa') return [];
  const eligible = (pool ?? []).filter(cluster => cluster.vsanArchitecture === architecture
    && (kind === 'COMPUTE' ? cluster.type === 'HCI' || cluster.type === 'COMPUTE' : cluster.type === kind));
  return ownedId && !eligible.some(cluster => cluster.id !== ownedId) ? [] : eligible;
}

/** Validate the whole list before the save pipeline allocates or updates any cluster. */
export function hasInvalidClusterReuse(project: VCPM.Project | null | undefined, domainId: number | undefined, workloads: VCPM.Workload[]): boolean {
  const domain = project?.data.workloadDomains?.find(d => d.id === domainId);
  return workloads.some(workload => {
    const dhci = workload.vcfDeploymentInputs?.deploymentType === 'dHCI';
    const owned = ownedClusterReferences(domain?.workloadList?.find(w => w.id === workload.id));
    const architecture = workload.storageConfiguration?.vsanArchitecture;
    const invalid = (mode: string | undefined, ref: VCPM.ClusterReference | null | undefined, ownId: string | undefined, storage: boolean) => {
      if (mode !== 'use-existing-cluster') return false;
      const pool = storage ? project?.data.clusterConfigurations : domain?.clusterConfigurations;
      const options = getEligibleClusterReuseOptions<VCPM.ReusableComputeCluster | VCPM.StorageCluster>(
        pool, storage ? 'STORAGE' : dhci ? 'COMPUTE' : 'HCI', architecture, ownId);
      return !ref?.id || !options.some(cluster => cluster.id === ref.id);
    };
    return invalid(dhci ? workload.computeClusterHardwareConfiguration?.hardwareConfigMode : workload.hardwareConfiguration?.hardwareConfigMode, workload.computeCluster, owned.compute?.id, false)
      || (dhci && invalid(workload.storageClusterHardwareConfiguration?.hardwareConfigMode, workload.storageCluster, owned.storage?.id, true));
  });
}

export function workloadDeleteWarningLines(project: VCPM.Project | null | undefined, domainId: number | undefined, workload: VCPM.Workload): string[] {
  const domain = project?.data.workloadDomains?.find(d => d.id === domainId);
  const dhci = workload.vcfDeploymentInputs?.deploymentType === 'dHCI';
  const rows: string[] = [];
  for (const storage of dhci ? [false, true] : [false]) {
    const ref = storage ? workload.storageCluster : workload.computeCluster;
    if (!ref?.id && !ref?.name) continue;
    const pool = storage ? project?.data.clusterConfigurations : domain?.clusterConfigurations;
    const entity = ref.id ? pool?.find(c => c.id === ref.id) : undefined;
    const label = `${dhci ? (storage ? 'Storage: ' : 'Compute: ') : ''}${entity?.name || ref.name || ref.id}`;
    if (!entity || !domain) {
      rows.push(`${label}: this workload will be removed; cluster details are unavailable.`);
      continue;
    }
    const domains = storage ? project?.data.workloadDomains ?? [] : [domain];
    const shared = domains.some(d => d.workloadList?.some(w =>
      !(d.id === domainId && w.id === workload.id) && (storage ? w.storageCluster : w.computeCluster)?.id === ref.id));
    rows.push(shared
      ? `${label}: this workload will be removed; the cluster will remain because other workloads use it.`
      : `${label}: this workload and its unused cluster configuration will be removed from this sizing project.`);
  }
  return rows;
}
